if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const configurationValid = window.AvonleaProductConfiguration?.validateForForm?.(this.form);
        if (configurationValid === false) return;

        const engraving = this.getSelectedEngraving();
        if (engraving?.invalid) return;
        if (engraving) {
          this.onEngravedSubmit(evt, engraving);
          return;
        }

        this.onStandardSubmit(evt);
      }

      onStandardSubmit(evt) {
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      onEngravedSubmit(evt, engraving) {
        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const formData = new FormData(this.form);
        const variantId = Number(formData.get('id'));
        const feeVariantId = Number(engraving.feeVariantId);

        if (!variantId || !feeVariantId) {
          this.handleErrorMessage('Engraving could not be configured. Please refresh the page or call the store.');
          this.finishEngravedSubmit(evt);
          return;
        }

        const groupId = this.createEngravingGroupId();
        const properties = this.collectLineItemProperties(formData);
        properties._avl_engraving_group = groupId;
        properties._avl_engraving_fee_variant = String(feeVariantId);

        const productItem = {
          id: variantId,
          quantity: 1,
          properties,
        };

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) productItem.selling_plan = Number(sellingPlan);

        const feeProperties = {
          For: this.dataset.avlProductTitle || document.querySelector('h1')?.textContent?.trim() || 'Personalized charm',
          _avl_engraving_group: groupId,
          _avl_engraving_fee: 'true',
          _avl_parent_variant_id: String(variantId),
          _avl_parent_sku: this.getCurrentSku(),
          _avl_engraving_line_1: engraving.lines[0] || '',
          _avl_engraving_line_2: engraving.lines[1] || '',
          _avl_engraving_line_3: engraving.lines[2] || '',
        };

        const items = [
          productItem,
          {
            id: feeVariantId,
            quantity: 1,
            properties: feeProperties,
          },
        ];

        const payload = { items };
        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify(payload);

        const linesUpdateDeferred = this.createCartLinesUpdateEventForItems(items);

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const data = await response.json();
            if (!response.ok || data.status) {
              throw Object.assign(new Error(data.description || data.message || 'Engraving could not be added.'), {
                response: data,
              });
            }
            return data;
          })
          .then((response) => {
            this.resolveCartLinesUpdate(linesUpdateDeferred);

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });

            CartPerformance.measure('add:paint-updated-sections', () => {
              this.cart.renderContents(response);
            });
          })
          .catch((error) => {
            console.error(error);
            const message = error.message || 'Engraving could not be added. Please try again or call the store.';
            this.handleErrorMessage(message);
            this.dispatchCartErrorEvent(message, 'INVALID');
            linesUpdateDeferred?.reject(error);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: variantId,
              errors: message,
              message,
            });
          })
          .finally(() => this.finishEngravedSubmit(evt));
      }

      finishEngravedSubmit(evt) {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        CartPerformance.measureFromEvent('add:user-action', evt);
      }

      getSelectedEngraving() {
        if (this.dataset.avlEngravingEnabled !== 'true') return null;

        const engravingRoot = Array.from(document.querySelectorAll('[data-avl-engraving]')).find(
          (node) => node.dataset.formId === this.form.id
        );
        const toggle = engravingRoot?.querySelector('[data-avl-engraving-toggle]');
        if (!engravingRoot || !toggle?.checked) return null;

        const lineInputs = Array.from(engravingRoot.querySelectorAll('[data-avl-engraving-line]'));
        const lineOne = lineInputs.find((input) => input.dataset.avlEngravingLine === '1');
        const confirmation = engravingRoot.querySelector('[data-avl-engraving-confirm]');
        const feeVariantId = this.dataset.avlEngravingFeeVariantId;

        lineOne?.setCustomValidity(lineOne.value.trim() ? '' : 'Enter at least one character on engraving line 1.');
        confirmation?.setCustomValidity(
          confirmation.checked ? '' : 'Confirm that you reviewed the engraving spelling and punctuation.'
        );

        if (!lineOne?.reportValidity() || !confirmation?.reportValidity()) {
          return { invalid: true };
        }

        if (!feeVariantId) {
          this.handleErrorMessage('Engraving is temporarily unavailable online. Please call the store for assistance.');
          return { invalid: true };
        }

        return {
          feeVariantId,
          lines: lineInputs.map((input) => input.value.trim().slice(0, 10)),
        };
      }

      collectLineItemProperties(formData) {
        const properties = {};
        for (const [name, rawValue] of formData.entries()) {
          const match = /^properties\[(.+)\]$/.exec(name);
          if (!match) continue;
          const value = String(rawValue ?? '').trim();
          if (value) properties[match[1]] = value;
        }
        return properties;
      }

      createEngravingGroupId() {
        if (window.crypto?.randomUUID) return `avl-${window.crypto.randomUUID()}`;
        return `avl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      }

      getCurrentSku() {
        const sectionId = this.dataset.sectionId;
        const skuNode = sectionId
          ? document.getElementById(`Sku-${sectionId}`) || document.querySelector(`[data-section="${sectionId}"] [data-avl-sku-text]`)
          : null;
        return skuNode?.textContent?.trim() || this.dataset.avlProductSku || 'Not listed';
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      createCartLinesUpdateEventForItems(items) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
